/**
 * Convert to Fabrication Tool
 * 
 * Converts design intent elements (Duct/Pipe) to LOD 400 Fabrication Parts.
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { FabricationManager } from '../core/FabricationManager';
import * as THREE from 'three';

export class ConvertToFabricationTool extends BaseTool {
    id = 'convert_fabrication';
    name = 'Convert to Fabrication';
    cursor = 'alias'; // Pointer alias to indicate modification

    private manager: FabricationManager;

    constructor() {
        super();
        this.manager = new FabricationManager();
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Find element to convert
        const result = this.getIntersection(event);
        if (!result) return;

        // Find the element at this location
        // We'll traverse all elements and check distance/bounds since we don't have a robust raycast-to-element-id here
        // But BaseTool has 'getIntersection' which returns a point. 
        // We really need 'raycast' against objects.
        // Assuming we can find the element ID from the event object in a real app, 
        // here we will iterate the BIM store to find a nearby Duct/Pipe.

        const hitPoint = result;
        const elements = Array.from(this.context.bimStore.project.elements.values());

        // Find closest Duct/Pipe within 0.5m
        const target = elements.find(el => {
            if (el.category !== 'Ducts' && el.category !== 'Pipes') return false;
            const geo = el.geometryData as any;
            if (!geo.path || geo.path.length < 2) return false;

            // Simple check: distance to start or end
            const start = new THREE.Vector3(...geo.path[0]);
            const end = new THREE.Vector3(...geo.path[1]);

            // Check if point is roughly on line segment
            const line = new THREE.Line3(start, end);
            const closest = new THREE.Vector3();
            line.closestPointToPoint(hitPoint, true, closest);

            return closest.distanceTo(hitPoint) < 0.2; // Tolerance
        });

        if (target) {
            this.convertElement(target);
        }
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void {
        // Optional: Highlight candidate
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private convertElement(element: any) {
        if (!this.context) return;

        console.log(`Converting ${element.category} (ID: ${element.id}) to Fabrication...`);

        // 1. Determine Service (Simplified mapping)
        const serviceName = element.category === 'Ducts' ? 'Supply Air 2in WG' : 'Chilled Water Supply';
        const service = this.manager.getService(serviceName);
        if (!service) return;

        // 2. Get Geometry
        const path = element.geometryData.path;
        const start = new THREE.Vector3(...path[0]);
        const end = new THREE.Vector3(...path[path.length - 1]);
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const totalLength = start.distanceTo(end);

        // 3. Delete Design Element
        this.context.bimStore.project.elements.delete(element.id);

        // 4. Create Straights (Split by standard length)
        // For demonstration, let's say standard length is 2.0m
        const straightCid = service.parts.find(p => p.type === 'Straight')?.cid || 1;
        const partDef = this.manager.getPartDefinition(straightCid);
        const stdLength = partDef?.defaultLength || 1.5;

        let currentDist = 0;
        const pStart = start.clone();

        while (currentDist < totalLength) {
            const remaining = totalLength - currentDist;
            const segmentLen = Math.min(remaining, stdLength);

            // Calculate center point for placement (Fabrication parts often placed by center or start, logic varies)
            // Here we assume position is start point for simplicity in this logic, 
            // though createPart might expect center. Let's pass start Position.

            // Actually createPart takes a position. Let's assume it puts the part's origin there.

            const part = this.manager.createPart(
                straightCid,
                serviceName,
                [pStart.x, pStart.y, pStart.z]
            );

            if (part) {
                // Orient
                // Calculate Euler angles from direction
                // Simplified: assuming horizontal XY plane rotation
                const angle = Math.atan2(direction.y, direction.x);
                part.geometryData.rotation = [0, 0, angle];

                // Override Parameter Length
                part.parameters['Length'] = segmentLen.toFixed(3);

                this.context.bimStore.addElement(part);
            }

            // Move forward
            pStart.add(direction.clone().multiplyScalar(segmentLen));
            currentDist += segmentLen;
        }

        console.log('Conversion Complete.');
    }
}
